import { create } from 'zustand';
import type { ReportDateBasis } from '@cg-projects/shared';

/** One-shot initial filters for the Reports overlay — how Home's stat tiles and
 *  workload rows become doors into the filtered work behind their numbers.
 *  ReportsPage applies it once and clears it, so a later plain openReports()
 *  starts from the defaults again.
 *  `assigneeId: 'none'` = unassigned-only. The server has no "no assignee"
 *  filter (the route clamps assigneeId to positive-or-null), so ReportsPage
 *  handles that value client-side and never sends it. */
export interface ReportsPreset {
  states?: Array<'open' | 'done' | 'cancelled'>;
  assigneeId?: number | 'none';
  dateBasis?: ReportDateBasis;
  dateFrom?: string;
  dateTo?: string;
}

interface UiState {
  createProjectOpen: boolean;
  openCreateProject: () => void;
  closeCreateProject: () => void;
  /** The request front door (v1.81.0) — "report a problem" from anywhere. */
  requestDoorOpen: boolean;
  openRequestDoor: () => void;
  closeRequestDoor: () => void;
  inboxOpen: boolean;
  openInbox: () => void;
  closeInbox: () => void;
  settingsOpen: boolean;
  openSettings: () => void;
  closeSettings: () => void;
  myWorkOpen: boolean;
  /** Optional view to land on — Home's Calendar button opens My Work already
   *  showing the month, instead of dropping you in the list to hunt for a toggle. */
  openMyWork: (view?: 'list' | 'calendar') => void;
  myWorkView: 'list' | 'calendar' | null;
  closeMyWork: () => void;
  reportsOpen: boolean;
  reportsQuery: string;
  reportsPreset: ReportsPreset | null;
  openReports: (initialQ?: string, preset?: ReportsPreset) => void;
  /** ReportsPage calls this the moment it applies the preset — one-shot by contract. */
  clearReportsPreset: () => void;
  closeReports: () => void;
  /** Media library — a full-screen panel like My Work/Reports. Opening it closes
   *  the other full-screen panels (and their opens close it): two fixed inset-0
   *  surfaces stacked means the top one silently eats the bottom one's Esc. */
  mediaOpen: boolean;
  openMedia: () => void;
  closeMedia: () => void;
  searchOpen: boolean;
  openSearch: () => void;
  closeSearch: () => void;
  toggleSearch: () => void;
  /** Keymap → board bridge: pressing 'c' on a board stamps a fresh timestamp
   *  here. BoardView consumes it by opening the add-task composer on the first
   *  visible stage (a timestamp, not a boolean, so every press is a distinct
   *  signal even with the composer already open). 0 = no request yet. */
  composerRequest: number;
  requestComposer: () => void;
  /** Tips & shortcuts popover — lifted out of AreaSidebar so the global '?'
   *  shortcut can toggle it from anywhere. */
  helpOpen: boolean;
  toggleHelp: () => void;
  closeHelp: () => void;
}

export const useUiStore = create<UiState>()((set) => ({
  createProjectOpen: false,
  openCreateProject: () => set({ createProjectOpen: true }),
  closeCreateProject: () => set({ createProjectOpen: false }),
  requestDoorOpen: false,
  openRequestDoor: () => set({ requestDoorOpen: true }),
  closeRequestDoor: () => set({ requestDoorOpen: false }),
  inboxOpen: false,
  openInbox: () => set({ inboxOpen: true, mediaOpen: false }),
  closeInbox: () => set({ inboxOpen: false }),
  settingsOpen: false,
  openSettings: () => set({ settingsOpen: true }),
  closeSettings: () => set({ settingsOpen: false }),
  myWorkOpen: false,
  myWorkView: null,
  openMyWork: (view) => set({ myWorkOpen: true, myWorkView: view ?? null, mediaOpen: false }),
  closeMyWork: () => set({ myWorkOpen: false, myWorkView: null }),
  reportsOpen: false,
  reportsQuery: '',
  reportsPreset: null,
  openReports: (initialQ, preset) => set({ reportsOpen: true, reportsQuery: initialQ ?? '', reportsPreset: preset ?? null, mediaOpen: false }),
  clearReportsPreset: () => set({ reportsPreset: null }),
  closeReports: () => set({ reportsOpen: false, reportsPreset: null }),
  mediaOpen: false,
  openMedia: () => set({
    mediaOpen: true,
    myWorkOpen: false, myWorkView: null,
    reportsOpen: false, reportsPreset: null,
    inboxOpen: false,
  }),
  closeMedia: () => set({ mediaOpen: false }),
  searchOpen: false,
  openSearch: () => set({ searchOpen: true }),
  closeSearch: () => set({ searchOpen: false }),
  toggleSearch: () => set((s) => ({ searchOpen: !s.searchOpen })),
  composerRequest: 0,
  requestComposer: () => set({ composerRequest: Date.now() }),
  helpOpen: false,
  toggleHelp: () => set((s) => ({ helpOpen: !s.helpOpen })),
  closeHelp: () => set({ helpOpen: false }),
}));
